// ==============================================================
// moduleQuizService.ts - Production Ready
// ==============================================================

import api from "./api";

// ==============================================================
// Type Definitions
// ==============================================================

export interface QuizSubmissionData {
  sessionId: string;
  moduleId: string;
  answers: { [questionId: string]: string | number };
  timeSpent: number;
  completedAt: number;
}

export interface QuizResult {
  sessionId: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  timeSpent: number;
  feedback: string[];
}

export interface StudentAnalyticsData {
  studentId: string;
  totalStudyTime: number;
  averageQuizScore: number;
  moduleProgresses: ModuleProgress[];
  strengthAreas: string[];
  improvementAreas: string[];
  lastUpdated: number;
}

export interface ModuleProgress {
  moduleId: string;
  moduleName: string;
  weekNumber: number;
  completionPercentage: number;
  sectionsCompleted: string[];
  quizCompleted: boolean;
  quizScore?: number;
  lastAccessed: number;
}

export interface QuestionData {
  _id: string;
  questionText: string;
  questionType: string;
  options?: string[];
  correctAnswer: string | number;
  points: number;
  skill: string;
  module: string;
}
export interface QuizAttempt {
  attemptNumber: number;
  submissionId: string;
  sessionId: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  totalPoints: number;
  earnedPoints: number;
  timeSpent: number;
  completedAt: number;
  gradedAt?: string;
}

// ==============================================================
// Quiz Session Management
// ==============================================================

/**
 * Start a new quiz session
 * @param sessionId - Unique session identifier
 * @param moduleId - Module identifier
 */
export const startQuizSession = async (sessionId: string, moduleId: string) => {
  const response = await api.post("/quiz/start-session", {
    sessionId,
    moduleId,
    startTime: Date.now(),
  });
  return response.data;
};

/**
 * Submit quiz answers to backend
 * @param submissionData - Quiz submission data including answers and timing
 */
export const submitQuizAnswers = async (submissionData: QuizSubmissionData) => {
  const response = await api.post(
    `/programs/modules/${submissionData.moduleId}/submit-quiz`,
    submissionData,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const getStudentQuizStats = async () => {
  const response = await api.get("/programs/student/quiz-stats");
  return response.data;
};

/**
 * Get quiz results for a completed session
 * @param sessionId - Session identifier
 */
export const getQuizResult = async (sessionId: string) => {
  const response = await api.get(`/quiz/result/${sessionId}`);
  return response.data;
};

// ==============================================================
// Module Questions
// ==============================================================

/**
 * Fetch questions for a specific module
 * @param moduleId - Module identifier (e.g., "eap_yellow_week1_module1")
 * @returns Quiz data with questions array
 */
export const getModuleQuestions = async (moduleId: string) => {
  console.log(`🔄 Fetching questions for module: ${moduleId}`);

  const response = await api.get(`/programs/module/${moduleId}/questions`);

  // Handle different possible response structures from backend
  const data = response.data?.data || response.data;
  const questions = data?.questions || data;

  // Validate we got questions
  if (!Array.isArray(questions) || questions.length === 0) {
    throw new Error(`No questions available for module ${moduleId}`);
  }

  console.log(`✅ Loaded ${questions.length} questions for ${moduleId}`);

  // Return in consistent format
  return {
    questions,
    totalQuestions: questions.length,
    totalPoints: questions.reduce(
      (sum: number, q: any) => sum + (q.points || 0),
      0
    ),
    estimatedTime: questions.length * 60000, // 1 minute per question
  };
};

/**
 * Fetch a single question by ID
 * @param questionId - Question identifier
 */
export const getQuestionById = async (questionId: string) => {
  const response = await api.get(`/quiz/question/${questionId}`);
  return response.data;
};

// ==============================================================
// Student Analytics
// ==============================================================

/**
 * Get analytics for a student
 * @param studentId - Optional student ID (defaults to current user)
 */
export const getStudentAnalytics = async (studentId?: string) => {
  const endpoint = studentId
    ? `/analytics/student/${studentId}`
    : `/analytics/student/me`;

  const response = await api.get(endpoint);
  return response.data;
};

/**
 * Get analytics for a specific module
 * @param moduleId - Module identifier
 * @param studentId - Optional student ID
 */
export const getModuleAnalytics = async (
  moduleId: string,
  studentId?: string
) => {
  const params = studentId ? `?studentId=${studentId}` : "";
  const response = await api.get(`/analytics/module/${moduleId}${params}`);
  return response.data;
};

/**
 * Get analytics for a specific week
 * @param weekNumber - Week number
 * @param programId - Program identifier
 */
export const getWeekAnalytics = async (
  weekNumber: number,
  programId: string
) => {
  const response = await api.get(`/analytics/week/${weekNumber}`, {
    params: { programId },
  });
  return response.data;
};

// ==============================================================
// Progress Tracking
// ==============================================================

/**
 * Update module progress for a student
 * @param moduleId - Module identifier
 * @param progressData - Progress data to update
 */
export const updateModuleProgress = async (
  moduleId: string,
  progressData: Partial<ModuleProgress>
) => {
  const response = await api.put(`/progress/module/${moduleId}`, progressData, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

/**
 * Get progress for a student across all modules
 * @param studentId - Optional student ID (defaults to current user)
 */
export const getStudentProgress = async (studentId?: string) => {
  const endpoint = studentId
    ? `/progress/student/${studentId}`
    : `/progress/student/me`;

  const response = await api.get(endpoint);
  return response.data;
};

// ==============================================================
// Quiz History
// ==============================================================

/**
 * Get quiz history for a student
 * @param studentId - Optional student ID
 * @param moduleId - Optional module ID to filter by
 */
export const getQuizHistory = async (studentId?: string, moduleId?: string) => {
  const params: any = {};
  if (studentId) params.studentId = studentId;
  if (moduleId) params.moduleId = moduleId;

  const response = await api.get(`/quiz/history`, { params });
  return response.data;
};

/**
 * Get quiz attempts for a specific module
 * @param moduleId - Module identifier
 */
export const getQuizAttempts = async (
  moduleId: string
): Promise<QuizAttempt[]> => {
  const response = await api.get(`/programs/quiz/attempts/${moduleId}`);
  return response.data.data || [];
};

// ==============================================================
// Study Analytics & Behavior Tracking
// ==============================================================

/**
 * Track study behavior for analytics
 * @param behaviorData - Behavior tracking data
 */
export const trackStudyBehavior = async (behaviorData: {
  moduleId: string;
  activityType: string;
  timeSpent: number;
  interactions: any[];
  metadata?: any;
}) => {
  const response = await api.post("/analytics/behavior", behaviorData, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

/**
 * Get personalized study recommendations for a student
 * @param studentId - Optional student ID (defaults to current user)
 */
export const getStudyRecommendations = async (studentId?: string) => {
  const endpoint = studentId
    ? `/recommendations/student/${studentId}`
    : `/recommendations/student/me`;

  const response = await api.get(endpoint);
  return response.data;
};

// ==============================================================
// Utility Functions
// ==============================================================

/**
 * Calculate quiz score based on correct answers
 * @param answers - User's answers
 * @param correctAnswers - Correct answers
 * @returns Score object with score percentage, correct count, and total
 */
export const calculateQuizScore = (
  answers: { [questionId: string]: string | number },
  correctAnswers: { [questionId: string]: string | number }
): { score: number; correct: number; total: number } => {
  const total = Object.keys(correctAnswers).length;
  let correct = 0;

  Object.entries(correctAnswers).forEach(([questionId, correctAnswer]) => {
    if (answers[questionId] === correctAnswer) {
      correct++;
    }
  });

  const score = total > 0 ? Math.round((correct / total) * 100) : 0;

  return { score, correct, total };
};

/**
 * Calculate time spent between two timestamps
 * @param startTime - Start timestamp in milliseconds
 * @param endTime - End timestamp in milliseconds
 * @returns Time spent in milliseconds
 */
export const calculateTimeSpent = (
  startTime: number,
  endTime: number
): number => {
  return Math.max(0, endTime - startTime);
};

/**
 * Format milliseconds into human-readable time string
 * @param milliseconds - Time in milliseconds
 * @returns Formatted time string (e.g., "1h 23m", "45m 12s", "30s")
 */
export const formatStudyTime = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

// ==============================================================
// Default Export (for backward compatibility)
// ==============================================================

export default {
  // Session management
  startQuizSession,
  submitQuizAnswers,
  getQuizResult,

  // Questions
  getModuleQuestions,
  getQuestionById,

  // Analytics
  getStudentAnalytics,
  getModuleAnalytics,
  getWeekAnalytics,

  // Progress
  updateModuleProgress,
  getStudentProgress,

  // History
  getQuizHistory,
  getQuizAttempts,

  // Behavior tracking
  trackStudyBehavior,
  getStudyRecommendations,

  // Utilities
  calculateQuizScore,
  calculateTimeSpent,
  formatStudyTime,
};
